import { FC, useCallback, useRef } from 'react';
import { Box } from '@altermaind/web/ui';
import { ClaimsComponent } from '@/_shared/components/Claims/Claims';
import { Roles } from '@/_shared/constants/roles';
import { useDialog } from '@/_shared/hooks/useDialog';
import { ContentType } from '@/swagger/constants/contents-orchestrator-enums';
import { postContentEndCall, postContentProgressCall } from '@/training/hardSkills/state';
import { DialogConfirmExitFromVideo } from '../DialogConfirmExitFromVideo';
import { DialogFeedbackVideo } from '../DialogFeedbackVideo';
import { Player } from './Player';

type Props = {
  id: string;
  src: string;
  open: boolean;
  startFrom?: number;
  onClose: () => void;
};

export const VideoPlayer: FC<Props> = ({ id, src, open, startFrom, onClose }) => {
  const { openDialog: openDialogConfirmExit } = useDialog('confirmExitFromVideoDialog');
  const { openDialog: openDialogFeedback } = useDialog('feedbackVideoDialog');
  const handleCancelExitRef = useRef<(() => void) | null>(null);

  const { execute: executePostContentEnd } = postContentEndCall.hook();
  const { execute: executePostContentProgress } = postContentProgressCall.hook();

  const handleCancelExitReady = useCallback((handleCancelExit: () => void) => {
    handleCancelExitRef.current = handleCancelExit;
  }, []);

  const handleCancelExit = useCallback(() => {
    handleCancelExitRef.current?.();
  }, []);

  const onVideoEndLearning = useCallback(() => {
    executePostContentEnd({
      contentId: id,
      successSaga: () => {
        openDialogFeedback();
      },
    });
  }, [id, executePostContentEnd, openDialogFeedback]);

  const onVideoProgress = useCallback(
    (activeSecond: number) => {
      if (activeSecond > 0) {
        executePostContentProgress({
          contentId: id,
          progress: {
            type: ContentType.VIDEO,
            seconds: activeSecond,
          },
        });
      }
    },
    [executePostContentProgress, id],
  );

  if (!open) {
    return null;
  }

  return (
    <Box
      width='100%'
      height='100vh'
      style={{
        background: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ClaimsComponent roles={[Roles.Admin, Roles.HR_Delegate]}>
        <Player
          src={src}
          onCloseVideo={openDialogConfirmExit}
          onCancelCloseVideoReady={handleCancelExitReady}
          resetOnEnd
        />
      </ClaimsComponent>
      <ClaimsComponent roles={[Roles.Employee, Roles.Manager]}>
        <>
          <Player
            src={src}
            startFrom={startFrom}
            onCloseVideo={openDialogConfirmExit}
            onCancelCloseVideoReady={handleCancelExitReady}
            onVideoEnd={onVideoEndLearning}
            onVideoProgress={onVideoProgress}
          />
          <DialogFeedbackVideo onClose={onClose} id={id} />
        </>
      </ClaimsComponent>
      <DialogConfirmExitFromVideo onClose={onClose} onCancel={handleCancelExit} />
    </Box>
  );
};
